/**
 * Whether a connection may upload new videos (#2350 AC 3.5).
 *
 * Google grants scopes per consent, so flipping the per-connection
 * ``video_uploads_enabled`` flag does not retroactively add
 * ``youtube.upload`` to a row that was authorized captions-only. The
 * backend says as much on `update_connection_settings`: the frontend
 * reads ``scopes_granted`` to decide whether to prompt for a reconnect.
 *
 * Both the Connections row and the publish dialog ask this question, so
 * the scope URL lives here rather than being spelled out twice.
 */

import type { Connection } from '@utils/types/connections/connection.ts';

export const YOUTUBE_UPLOAD_SCOPE =
  'https://www.googleapis.com/auth/youtube.upload';

/** True when Google granted this connection the new-video upload scope. */
export const hasYouTubeUploadScope = (
  connection?: Connection | null
): boolean =>
  !!connection?.defaults?.scopes_granted?.includes(YOUTUBE_UPLOAD_SCOPE);

/**
 * True when uploads are switched on for this connection but the grant
 * predates that choice — the state AC 3.5 asks us to explain inline,
 * because the toggle looks ready while every upload would be refused.
 */
export const needsReconnectForUpload = (
  connection?: Connection | null
): boolean =>
  !!connection?.video_uploads_enabled && !hasYouTubeUploadScope(connection);
